const crawlerUserAgents = [
  'googlebot',
  'Google Page Speed',
  'developers.google.com/+/web/snippet',
  'Chrome-Lighthouse'
  // 'Yahoo! Slurp',
  // 'bingbot',
  // 'yandex',
  // 'baiduspider',
  // 'facebookexternalhit',
  // 'twitterbot',
  // 'rogerbot',
  // 'linkedinbot',
  // 'embedly',
  // 'quora link preview',
  // 'showyoubot',
  // 'outbrain',
  // 'pinterest/0.',
  // 'slackbot',
  // 'vkShare',
  // 'W3C_Validator',
  // 'redditbot',
  // 'Applebot',
  // 'WhatsApp',
  // 'flipboard',
  // 'tumblr',
  // 'bitlybot',
  // 'SkypeUriPreview',
  // 'nuzzel',
  // 'Discordbot',
  // 'Qwantify',
  // 'pinterestbot',
  // 'Bitrix link preview',
  // 'XING-contenttabreceiver',
]

export const isBot = (userAgent: string | undefined) => {
  const isBotUA = crawlerUserAgents.some((crawlerUserAgent) => {
    return userAgent?.toLowerCase().includes(crawlerUserAgent.toLowerCase())
  })
  return isBotUA
}

// Nuxt2 payload precedence; skip empty/oversized candidates before selecting.
export const ATTRIBUTION_PARAMS = ['demoid', 'dbwsid', 'dbwsdemoid'] as const

export type AttributionParam = typeof ATTRIBUTION_PARAMS[number]

// Longest value accepted as a demonstrator id - nuxt2's `length <= 7` guard.
const MAX_ATTRIBUTION_VALUE_LENGTH = 7

export const isAttributionKey = (key: string): boolean =>
  ATTRIBUTION_PARAMS.some(param => param === key.toLowerCase())

export interface AttributionSelection {
  key: AttributionParam
  value: string
}

export const findAttributionParam = (
  query: Record<string, unknown>
): AttributionSelection | null => {
  const keys = Object.keys(query)
  for (const key of ATTRIBUTION_PARAMS) {
    const rawKey = keys.find(candidate => candidate.toLowerCase() === key)
    if (rawKey !== undefined) {
      const rawValue = query[rawKey]
      const value = String((Array.isArray(rawValue) ? rawValue[0] : rawValue) ?? '')
      if (value.length > 0 && value.length <= MAX_ATTRIBUTION_VALUE_LENGTH) return { key, value }
    }
  }
  return null
}

// Presence is independent of validity: bare/invalid referral keys still explain
// the request to SEO and pre-attribution telemetry.
export const hasAttributionParam = (query: Record<string, unknown>): boolean =>
  Object.keys(query).some(isAttributionKey)

// A referer is a search engine only when its host serves results pages. For
// Google, Yandex and Yahoo a whole domain is too wide: they keep mail, docs,
// sites and news beside search, and a demonstrator link opened from one of
// those is a real referral, not a SERP. nuxt2 matches any host under the
// engine's second-level domain, mail included.

// Hosts that serve nothing but search, so their subdomains are regional, mobile
// or lite variants of it: uk.search.yahoo.com, html.duckduckgo.com, m.bing.com.
const SEARCH_ONLY_DOMAINS = [
  'bing.com', 'bing.co.uk', 'duckduckgo.com', 'ecosia.org', 'search.brave.com', 'search.yahoo.com', 'search.yahoo.co.jp'
]

// Google and Yandex share each domain with mail, docs and sites, so only their
// results hosts count: the bare domain or `www.`, plus Google's image, Lens and
// custom-search hosts. Both run one site per country - google.de, google.co.uk,
// yandex.kz - so the suffix is `com` or a two-letter ccTLD, optionally under
// `co.`/`com.`.
const PORTAL_SEARCH_HOST = /^(?:(?:(?:www|images|lens|cse)\.)?google|(?:www\.)?yandex)\.(?:com|(?:com?\.)?[a-z]{2})$/

export const isSearchEngineReferer = (referer: string | undefined): boolean => {
  if (!referer) {
    return false
  }
  try {
    const hostname = new URL(referer).hostname.toLowerCase().replace(/\.$/, '')
    return PORTAL_SEARCH_HOST.test(hostname) ||
      SEARCH_ONLY_DOMAINS.some(domain => hostname === domain || hostname.endsWith(`.${domain}`))
  } catch {
    return false
  }
}
